// @givmo/app-charity — the `/org` family's entry gate, a CAPABILITY check (shell contract v3, the
// build law: permission checks, never admin checks).
//
// ══ ONE GATE PER PAGE, KEYED ON THE CAPABILITY THE PAGE DECLARES ═══════════════════════════════
//
// Each Admin Console page names its own capability (`org-pages.ts`: Apps `write_settings` · Team
// access `write_member_access` · Activity `read_events`), read off the ONE standing read's
// class-independent `capabilities` list — never the membership's tier. A Global Admin holds all
// three by tier; a custom permission set may hold one without the others, and that person opens
// exactly the pages their strings admit.
//
// Someone who does not hold the page's capability has not found a page they may not use — they have
// found the wrong page, and the right one is `/apps` (their own landing statement). So this replaces
// the location instead of rendering a considered refusal.
//
// ⚠ A REDIRECT IS A POSITIVE ACT ON A POSITIVE ANSWER. The list is `null` while the read reported
// none — a dark console (404), a backend predating the field, an errored read, a read still in
// flight — and a `null` list RENDERS. Redirecting on it would bounce a real Global Admin out of
// their own organization because a request timed out; the package's panels still gate themselves
// on their own read, so nothing leaks. Only a SETTLED list that lacks the capability redirects.

import { useEffect, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { holdsCapability } from '@givmo/org-console';
import { useCharityConsole } from '../../console/charity-console-context';
import { APPS_ROUTE } from '../../console/console-routes';

export interface RequireCapabilityProps {
  /** The capability the page declares — the wire's snake_case value. */
  capability: string;
  children: ReactNode;
}

/** Whether a settled capability list admits the page; an unreported list admits (see the header). */
export function capabilityAdmits(
  capabilities: readonly string[] | null,
  capability: string,
): boolean {
  return capabilities === null || holdsCapability(capabilities, capability);
}

export function RequireCapability({ capability, children }: RequireCapabilityProps) {
  const { read } = useCharityConsole();
  const navigate = useNavigate();
  const admitted = capabilityAdmits(read.capabilities, capability);

  useEffect(() => {
    if (admitted) return;
    void navigate({ to: APPS_ROUTE, replace: true });
  }, [admitted, navigate]);

  // Nothing at all for the one frame before the redirect lands. A refusal notice here would be shown
  // to somebody who is on their way somewhere else.
  if (!admitted) return null;
  return <>{children}</>;
}
